import { useState } from "react";
import { ChevronDown } from "lucide-react";

const INK = {
  High: "text-emerald-700",
  Directional: "text-amber-700",
  "Insufficient data": "text-rose-700",
};

const DOT = {
  High: "bg-emerald-600",
  Directional: "bg-amber-500",
  "Insufficient data": "bg-rose-600",
};

const fyRange = (fys) => {
  if (!fys || fys.length === 0) return null;
  return fys.length === 1 ? fys[0] : `${fys[0]}–${fys[fys.length - 1]}`;
};

/**
 * One muted line: badge · sample · years. Answer-specific caveats (at most two) sit directly
 * beneath when `inlineCaveats` is set; standing methodology is behind "How this is computed".
 * Payloads without the caveat/methodology split fall back to the full reasons list.
 */
export const TrustBadge = ({ answerability, compact = false, inlineCaveats = false, testId = "trust-badge" }) => {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState(false);
  if (!answerability) return null;
  const level = answerability.trust_badge || "Insufficient data";
  const ink = INK[level] || INK["Insufficient data"];
  const dot = DOT[level] || DOT["Insufficient data"];
  const geo = answerability.geo || {};
  const hasSplit = Array.isArray(answerability.caveats);
  const caveats = hasSplit ? answerability.caveats : answerability.reasons || [];
  const methodology = hasSplit ? answerability.methodology || [] : [];
  const unlocks = answerability.unlocks_with || [];
  const n = answerability.sample_size;
  const fy = fyRange(answerability.fy_range);

  const List = ({ items, muted }) => (
    <ul className="space-y-1">
      {items.map((r, i) => (
        <li key={i} className={`text-[12px] leading-relaxed flex gap-2 ${muted ? "text-stone-500" : "text-stone-600"}`}>
          <span className={`w-1 h-1 rounded-full mt-[7px] shrink-0 ${muted ? "bg-stone-300" : dot}`} />
          <span>{r}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <div data-testid={testId}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="inline-flex items-center gap-1.5 text-[12px] text-stone-500 hover:text-stone-800 transition-colors text-left"
        data-testid={`${testId}-toggle`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
        <span className={`font-semibold ${ink}`} data-testid={`${testId}-level`}>{level}</span>
        {!compact && (
          <span className="num" data-testid={`${testId}-stats`}>
            {n !== undefined && n !== null ? ` · n=${n}` : ""}
            {fy ? ` · ${fy}` : ""}
            {answerability.k !== null && answerability.k !== undefined ? ` · k=${answerability.k}` : ""}
          </span>
        )}
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {inlineCaveats && !open && caveats.length > 0 && (
        <div className="mt-1.5" data-testid={`${testId}-caveats`}>
          <List items={caveats} />
        </div>
      )}

      {open && (
        <div className="mt-2 space-y-2.5 border-l-2 border-stone-200 pl-3" data-testid={`${testId}-reasons`}>
          {caveats.length > 0 ? <List items={caveats} /> : (
            <p className="text-[12px] text-stone-500">No caveat specific to this answer.</p>
          )}
          {unlocks.length > 0 && (
            <div className="text-[12px] text-stone-600 leading-relaxed">
              <div className="font-semibold mb-0.5">What would unlock this</div>
              <ul className="space-y-0.5">
                {unlocks.map((u, i) => <li key={i}>{u}</li>)}
              </ul>
            </div>
          )}
          {methodology.length > 0 && (
            <div>
              <button type="button" onClick={(e) => { e.stopPropagation(); setMethod(!method); }}
                      data-testid={`${testId}-methodology-toggle`}
                      className="text-[12px] font-semibold text-stone-500 hover:text-stone-800 flex items-center gap-1">
                How this is computed ({methodology.length})
                <ChevronDown className={`w-3 h-3 transition-transform ${method ? "rotate-180" : ""}`} />
              </button>
              {method && (
                <div className="mt-1.5" data-testid={`${testId}-methodology`}>
                  <List items={methodology} muted />
                </div>
              )}
            </div>
          )}
          {geo.levels && Object.keys(geo.levels).length > 0 && (
            <div className="num text-[12px] text-stone-500">
              geo match: {Object.entries(geo.levels).map(([k, v]) => `${k}=${v}`).join(" · ")} ·
              exact {geo.exact_share_pct}%
            </div>
          )}
        </div>
      )}
    </div>
  );
};
